import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";

import { isReactNativeWebView } from "@/utils/useWebViewBridge";

import {
  MOBILE_HEADER_TOP_GAP,
  getMobileContentTopInset,
  getMobileHeaderOffset,
  getMobileToolbarHeight,
} from "./headerLayoutUtils";

export type MobileHeaderPanel = "notifications" | "userMenu";

interface HeaderCollapseContextValue {
  isHeaderCollapsed: boolean;
  toggleHeaderCollapse: () => void;
  mobileHeaderSpacerHeight: string | undefined;
  mobileHeaderOffset: string;
  mobileOpenPanel: MobileHeaderPanel | null;
  toggleMobileHeaderPanel: (panel: MobileHeaderPanel) => void;
  closeMobileHeaderPanel: () => void;
  isHeaderPanelOpen: boolean;
}

const HeaderCollapseContext = createContext<HeaderCollapseContextValue>({
  isHeaderCollapsed: false,
  toggleHeaderCollapse: () => undefined,
  mobileHeaderSpacerHeight: undefined,
  mobileHeaderOffset: "56px",
  mobileOpenPanel: null,
  toggleMobileHeaderPanel: () => undefined,
  closeMobileHeaderPanel: () => undefined,
  isHeaderPanelOpen: false,
});

export function HeaderCollapseProvider({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [mobileOpenPanel, setMobileOpenPanel] =
    useState<MobileHeaderPanel | null>(null);

  useEffect(() => {
    if (isMediumScreen) {
      setIsHeaderCollapsed(false);
      setMobileOpenPanel(null);
    }
  }, [isMediumScreen]);

  const toggleHeaderCollapse = useCallback(() => {
    setIsHeaderCollapsed((prev) => !prev);
  }, []);

  const toggleMobileHeaderPanel = useCallback((panel: MobileHeaderPanel) => {
    setMobileOpenPanel((prev) => (prev === panel ? null : panel));
  }, []);

  const closeMobileHeaderPanel = useCallback(() => {
    setMobileOpenPanel(null);
  }, []);

  const isHeaderPanelOpen = mobileOpenPanel !== null;

  const value = useMemo<HeaderCollapseContextValue>(() => {
    // WebView do app: safe-area já aplicado no nativo — não duplicar no CSS.
    const includeSafeAreaTop = !isReactNativeWebView();
    const mobileHeaderOffset = getMobileHeaderOffset(
      isHeaderCollapsed,
      theme,
      includeSafeAreaTop,
    );
    const mobileHeaderSpacerHeight = !isMediumScreen
      ? getMobileContentTopInset(theme, includeSafeAreaTop)
      : undefined;

    return {
      isHeaderCollapsed,
      toggleHeaderCollapse,
      mobileHeaderSpacerHeight,
      mobileHeaderOffset,
      mobileOpenPanel,
      toggleMobileHeaderPanel,
      closeMobileHeaderPanel,
      isHeaderPanelOpen,
    };
  }, [
    isHeaderCollapsed,
    isHeaderPanelOpen,
    isMediumScreen,
    mobileOpenPanel,
    closeMobileHeaderPanel,
    toggleMobileHeaderPanel,
    theme,
    toggleHeaderCollapse,
  ]);

  return (
    <HeaderCollapseContext.Provider value={value}>
      {children}
    </HeaderCollapseContext.Provider>
  );
}

export function useHeaderCollapse() {
  return useContext(HeaderCollapseContext);
}

export { MOBILE_HEADER_TOP_GAP, getMobileToolbarHeight };
